import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrSubscriptionResponse } from '@/lib/api';

interface UseSubscriptionReturn {
  subscription: GrSubscriptionResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useSubscription(): UseSubscriptionReturn {
  const [subscription, setSubscription] = useState<GrSubscriptionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchSubscription = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.getGameRewardsSubscription();
      if (fetchIdRef.current !== id) return;
      setSubscription(result.subscription);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load subscription');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { subscription, loading, error, refresh };
}
